import React from 'react';
import styled from '@emotion/styled';
import Img01 from 'ui/images/top/topService_img01@2x.png';
import Img02 from 'ui/images/top/topService_img02@2x.png';
import Img03 from 'ui/images/top/kv_simg_01.png';
import Img04 from 'ui/images/top/topService_img04@2x.png';
import AboutManyPcImg from 'ui/images/top/about_many_dark_blue_pc.svg';
import AboutManySpImg from 'ui/images/top/about_many_dark_blue_sp.svg';
import { mqMin } from 'utils/responsive';

const Container = styled.section`
    background-color: var(--lightblue);
    padding: ${({ showTitle }: { showTitle: boolean }) => (showTitle ? '5rem 0 1rem 0' : '2rem')};
    img {
        max-width: 100%;
    }

    ${mqMin('NARROW')} {
        padding: ${({ showTitle }: { showTitle: boolean }) => (showTitle ? '7rem 0 2rem 0' : '2rem')};
    }
`;

const BUBBLE_COLORS = ['#61BAE6', '#4AB0E2', '#3399D9', '#2188D0'] as const;

const DescriptionContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.7rem;
    width: 92%;
    margin: ${({ showTitle }: { showTitle: boolean }) => (showTitle ? '0 auto 42px auto' : '0 auto')};

    ${mqMin('NARROW')} {
        gap: 2.4rem;
        width: 94%;
        max-width: 720px;
        margin: 0 auto;
    }
`;

const DescriptionItem = styled.div`
    position: relative;
    width: 100%;
    margin-bottom: -1.25rem;
    z-index: 1;

    &:nth-of-type(1) {
        z-index: 4;
    }
    &:nth-of-type(2) {
        z-index: 3;
    }
    &:nth-of-type(3) {
        z-index: 2;
    }
    &:nth-of-type(4) {
        z-index: 1;
    }
    background-color: ${BUBBLE_COLORS[0]};
    border-radius: 20px;
    padding: 28px 24px 32px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    opacity: 0;
    transform: translateY(30px);
    transition:
        transform 1s cubic-bezier(0.25, 1, 0.5, 1),
        opacity 1s cubic-bezier(0.25, 1, 0.5, 1);

    ${BUBBLE_COLORS.map(
        (color, i) => `
    &:nth-of-type(${i + 1}) {
        background-color: ${color};
        &::after {
            border-color: ${color} transparent transparent transparent;
        }
    }
`,
    ).join('')}

    &::after {
        content: '';
        position: absolute;
        bottom: -22px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 24px 24px 0 24px;
        filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.05));
    }

    ${mqMin('NARROW')} {
        display: flex;
        align-items: flex-start;
        gap: 2.5rem;
        margin-bottom: -1.75rem;
        padding: 36px 40px 44px;
        border-radius: 24px;

        &::after {
            bottom: -26px;
            border-width: 28px 28px 0 28px;
        }
    }

    &:last-of-type {
        margin-bottom: 0;

        &::after {
            display: none;
        }
    }
`;

const BubbleContent = styled.div`
    flex: 1;
    min-width: 0;

    ${mqMin('NARROW')} {
        order: 0;
    }
`;

const ImgWrapper = styled.div`
    flex-shrink: 0;
    margin-bottom: 16px;

    ${mqMin('NARROW')} {
        margin-bottom: 0;
        order: 1;
    }
`;

const ImgItem = styled.img`
    width: auto;
    height: 110px;
    margin: 0 auto;
    display: block;

    ${mqMin('NARROW')} {
        height: 140px;
    }
`;

const H3 = styled.h3`
    text-align: left;
    font-size: 1.2rem;
    color: #fff;
    margin-bottom: 4px;

    &::after {
        content: '';
        display: block;
        width: 32px;
        height: 2px;
        background-color: #fff;
        margin: 10px 0 14px;
    }

    ${mqMin('SP')} {
        font-size: 1.3rem;
    }

    ${mqMin('NARROW')} {
        font-size: 2rem;
        margin-bottom: 6px;
    }
`;

const Text = styled.p`
    padding: 0;
    text-align: left;
    font-size: 0.8rem;
    line-height: 1.7em;
    letter-spacing: 0.05em;
    color: #fff;

    ${mqMin('SP')} {
        font-size: 1.1rem;
    }

    ${mqMin('NARROW')} {
        font-size: 1.05rem;
    }
`;

const DescriptionText = styled(Text)`
    font-size: 0.7rem;
    ${mqMin('SP')} {
        font-size: 0.8rem;
    }
`;
const ManyPcTitle = styled.img`
    display: none;
    width: 29rem;
    margin: 0 auto 3rem auto;

    ${mqMin('NARROW')} {
        display: block;
    }
`;

const ManySpTitle = styled.img`
    display: block;
    width: 10rem;
    margin: 0 auto 3rem auto;

    ${mqMin('SP')} {
        width: 15rem;
    }

    ${mqMin('NARROW')} {
        display: none;
    }
`;

interface AboutServiceProps {
    showTitle?: boolean;
}

export const AboutService: React.FC<AboutServiceProps> = ({ showTitle = true }) => {
    return (
        <Container showTitle={showTitle} id="about-service">
            {showTitle && (
                <>
                    <ManyPcTitle src={AboutManyPcImg} alt="MAnyとは" />
                    <ManySpTitle src={AboutManySpImg} alt="MAnyとは" />
                </>
            )}
            <DescriptionContainer showTitle={showTitle} className="fadeinLi">
                <DescriptionItem>
                    <ImgWrapper>
                        <ImgItem src={Img01} alt="STEP1のイメージ" />
                    </ImgWrapper>
                    <BubbleContent>
                        <H3>STEP01</H3>
                        <Text>
                            事業承継に関する不安・質問・知りたいことなどを登録
                            <br />
                            <DescriptionText>（投稿時は匿名となるので、貴社名は公開されません）</DescriptionText>
                        </Text>
                    </BubbleContent>
                </DescriptionItem>
                <DescriptionItem>
                    <ImgWrapper>
                        <ImgItem src={Img02} alt="STEP2のイメージ" />
                    </ImgWrapper>
                    <BubbleContent>
                        <H3>STEP02</H3>
                        <Text>事業承継専門家から回答オファーが来る</Text>
                    </BubbleContent>
                </DescriptionItem>
                <DescriptionItem>
                    <ImgWrapper>
                        <ImgItem src={Img03} alt="STEP3のイメージ" />
                    </ImgWrapper>
                    <BubbleContent>
                        <H3>STEP03</H3>
                        <Text>
                            オファーを確認し、話を聞いてみたい専門家からのオファーを了承する
                            <br />
                            <DescriptionText>
                                ※当サービスに登録されている事業承継専門家は、秘密保持（守秘義務）に同意済みの専門家です。
                            </DescriptionText>
                        </Text>
                    </BubbleContent>
                </DescriptionItem>
                <DescriptionItem>
                    <ImgWrapper>
                        <ImgItem src={Img04} alt="STEP4のイメージ" />
                    </ImgWrapper>
                    <BubbleContent>
                        <H3>STEP04</H3>
                        <Text>事業承継専門家へ無料で相談。情報収集＆比較検討</Text>
                        <br />
                        <DescriptionText>
                            ※複数社からの情報を比較することで、納得のいく事業承継の意思決定を行うことが可能
                        </DescriptionText>
                    </BubbleContent>
                </DescriptionItem>
            </DescriptionContainer>
        </Container>
    );
};
